import {
  computePreviousEndingAtFromNewStart,
  dateObjectToMs,
  getCondominiumToShorten,
  getPreviousCondominiumForNewDraft,
  hasCondominiumGapAfterPrevious,
  isCondominiumEndingAtSet,
  isHistoricalCondominiumMissingEndDate,
  needsShortenPreviousCondominium,
  startsBeforePreviousCondominium,
  type CondominiumOwnershipRow
} from './condominiumUtils'

/** Onboarding rows may carry runtime draft ids that are not in the generated API types. */
export type CondominiumWithDraftId = CondominiumOwnershipRow

export type CondominiumDateConflictKey =
  | 'message.savingErrors.tenancyEndAtCanNotBeDeleted'
  | 'message.addTenancy.startsBeforePrevious'
  | 'message.savingErrors.condominiumGap'
  | 'message.savingErrors.condominiumInactiveNeedsOwnerEndDate'
  | 'message.savingErrors.condominiumEndAtCanNotBeDeleted'

export type CondominiumDateConflictResult = {
  messageKeys: CondominiumDateConflictKey[]
  needsShortenPrevious?: { objectId: string }
}

function isSameCondominiumRow(a: CondominiumWithDraftId, b: CondominiumWithDraftId): boolean {
  if (!a || !b) {
    return false
  }
  if (a.id && b.id && a.id === b.id) {
    return true
  }
  return !!(a.internalId && b.internalId && a.internalId === b.internalId)
}

function getNewDraftConflictResult(
  allCondominiums: CondominiumWithDraftId[],
  objectId: string
): CondominiumDateConflictResult {
  const messageKeys: CondominiumDateConflictKey[] = []
  const newDraft = allCondominiums.find(c => c.internalId && c.objectId === objectId)
  if (!newDraft?.startingAt) {
    return { messageKeys }
  }

  const previous = getPreviousCondominiumForNewDraft(allCondominiums, objectId)
  const historicalMissingEnd = previous &&
    isHistoricalCondominiumMissingEndDate(
      newDraft.startingAt,
      newDraft.endingAt,
      previous.startingAt
    )

  if (historicalMissingEnd) {
    messageKeys.push('message.savingErrors.tenancyEndAtCanNotBeDeleted')
    return { messageKeys }
  }

  if (startsBeforePreviousCondominium(newDraft, previous)) {
    messageKeys.push('message.addTenancy.startsBeforePrevious')
    return { messageKeys }
  }

  if (needsShortenPreviousCondominium(newDraft, previous)) {
    const expectedEnd = computePreviousEndingAtFromNewStart(newDraft.startingAt)
    const previousMatchesShorten = previous && expectedEnd &&
      JSON.stringify(previous.endingAt) === JSON.stringify(expectedEnd)

    if (!previousMatchesShorten) {
      return { messageKeys, needsShortenPrevious: { objectId } }
    }
    return { messageKeys }
  }

  if (hasCondominiumGapAfterPrevious(newDraft, previous)) {
    messageKeys.push('message.savingErrors.condominiumGap')
  }

  return { messageKeys }
}

function getOpenEndedPersistedConflictKeys(
  allCondominiums: CondominiumWithDraftId[],
  objectId: string,
  condominium: CondominiumWithDraftId,
  hasNewDraft: boolean
): CondominiumDateConflictKey[] {
  const errors: CondominiumDateConflictKey[] = []
  if (condominium.internalId || isCondominiumEndingAtSet(condominium.endingAt) || !condominium.startingAt) {
    return errors
  }

  const newDraft = hasNewDraft
    ? allCondominiums.find(c => c.internalId && c.objectId === objectId)
    : null
  const skipEndDateChecksForOpenPrevious = !!newDraft &&
    !!getCondominiumToShorten(allCondominiums, objectId, newDraft) &&
    !isCondominiumEndingAtSet(condominium.endingAt)

  if (skipEndDateChecksForOpenPrevious) {
    return errors
  }

  const startingAtMs = dateObjectToMs(condominium.startingAt)
  if (startingAtMs === null) {
    return errors
  }

  const sortedOthers = allCondominiums
    .filter(c => c.objectId === objectId && !isSameCondominiumRow(c, condominium))
    .sort((a, b) => (dateObjectToMs(b.startingAt) || 0) - (dateObjectToMs(a.startingAt) || 0))

  let isChecked = false
  for (const other of sortedOthers) {
    if (other.objectId !== objectId) {
      continue
    }
    const otherEndMs = dateObjectToMs(other.endingAt)
    if (otherEndMs !== null && startingAtMs <= otherEndMs) {
      errors.push('message.savingErrors.condominiumEndAtCanNotBeDeleted')
      break
    }
    const endPlusDay = otherEndMs !== null ? otherEndMs + 86400000 : null
    if (isCondominiumEndingAtSet(other.endingAt) && endPlusDay !== null &&
      startingAtMs > endPlusDay && !isChecked) {
      errors.push('message.savingErrors.condominiumGap')
      break
    }
    isChecked = true
    break
  }

  return errors
}

/**
 * Collect cross-ownership date conflict message keys for a single object.
 * Mirrors the rules in CreateObjectsTab.isInvalid() so Objects and Condominium tabs stay consistent.
 */
export function getCondominiumDateConflictForObject(
  allCondominiums: CondominiumWithDraftId[],
  objectId: string,
  object: { inactiveBy?: unknown } | null | undefined
): CondominiumDateConflictResult {
  const messageKeys: CondominiumDateConflictKey[] = []
  let needsShortenPrevious: { objectId: string } | undefined

  const objectCondominiums = allCondominiums.filter(c => c && c.objectId === objectId)
  if (objectCondominiums.length === 0) {
    return { messageKeys }
  }

  const sortedByStart = [...objectCondominiums].sort(
    (a, b) => (dateObjectToMs(b.startingAt) || 0) - (dateObjectToMs(a.startingAt) || 0)
  )
  const latestCondominium = sortedByStart[0] ?? null

  if (object?.inactiveBy && (!latestCondominium || !isCondominiumEndingAtSet(latestCondominium.endingAt))) {
    messageKeys.push('message.savingErrors.condominiumInactiveNeedsOwnerEndDate')
  }

  const hasNewDraft = objectCondominiums.some(c => c.internalId)
  const draftResult = getNewDraftConflictResult(allCondominiums, objectId)
  messageKeys.push(...draftResult.messageKeys)
  if (draftResult.needsShortenPrevious) {
    needsShortenPrevious = draftResult.needsShortenPrevious
  }

  for (const condominium of objectCondominiums) {
    const persistedErrors = getOpenEndedPersistedConflictKeys(
      allCondominiums,
      objectId,
      condominium,
      hasNewDraft
    )
    messageKeys.push(...persistedErrors)
  }

  return {
    messageKeys: [...new Set(messageKeys)],
    needsShortenPrevious
  }
}

/** @deprecated Use getCondominiumDateConflictForObject — kept as alias for readability at call sites. */
export function getCondominiumDateConflictMessageKeys(
  allCondominiums: CondominiumWithDraftId[],
  objectId: string,
  object: { inactiveBy?: unknown } | null | undefined
): CondominiumDateConflictResult {
  return getCondominiumDateConflictForObject(allCondominiums, objectId, object)
}
